/**
 * Data and rules for the New project form (EnhancedSubmitEditingRequest):
 * the brief's shape, the quick-start templates, and per-step validation.
 * Kept free of React so the rules can be tested on their own.
 */

export type SourceType = 'SINGLE' | 'MULTICAM';

export interface EditingRequestInput {
  projectName: string;
  description: string;
  comments?: string;
  videoName: string;
  deadline?: string;
  priority?: string;
  estimatedComplexity?: string;
  targetAudience?: string;
  desiredStyle?: string;
  referenceLinks?: string[];
  brandGuidelines?: string;
  deliverables?: string[];
  budget?: string;
  musicPreference?: string;
  colorPreference?: string;
  aiAssistance?: boolean;
  // Legacy fields from the backend schema (no inputs, still sent when set).
  timing?: string;
  overlay?: string;
  textInstructions?: string;
}

export const EMPTY_REQUEST: EditingRequestInput = {
  videoName: '',
  projectName: '',
  description: '',
  comments: '',
  deadline: '',
  priority: 'MEDIUM',
  estimatedComplexity: '',
  targetAudience: '',
  desiredStyle: '',
  referenceLinks: [],
  brandGuidelines: '',
  deliverables: [],
  budget: '',
  musicPreference: '',
  colorPreference: '',
  aiAssistance: false,
};

export const STEP_LABELS = ['Project basics', 'Creative brief', 'Media assets', 'Review and submit'] as const;
export const REVIEW_STEP = STEP_LABELS.length - 1;
/** The step that holds the uploaders; it stays mounted once visited so transfers can finish. */
export const ASSETS_STEP = 2;

export const PRIORITY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'URGENT', label: 'Urgent' },
];

export const COMPLEXITY_OPTIONS = ['Simple', 'Moderate', 'Complex'] as const;

/** Suggestions for the deliverables field; creators can also type their own. */
export const DELIVERABLE_SUGGESTIONS = [
  'Full HD (16:9)',
  '4K (16:9)',
  'Instagram Reel (9:16)',
  'TikTok (9:16)',
  'YouTube Short (9:16)',
  'Square (1:1)',
  'Presentation format',
  'Custom format',
];

/** The brief fields a template fills. Every one of them has a visible input. */
export type TemplateField = 'desiredStyle' | 'musicPreference' | 'deliverables' | 'estimatedComplexity';
export const TEMPLATE_FIELDS: TemplateField[] = ['desiredStyle', 'musicPreference', 'deliverables', 'estimatedComplexity'];

export const TEMPLATE_FIELD_LABELS: Record<TemplateField, string> = {
  desiredStyle: 'style',
  musicPreference: 'music',
  deliverables: 'deliverables',
  estimatedComplexity: 'complexity',
};

export interface ProjectTemplate {
  id: string;
  name: string;
  description: string;
  settings: Required<Pick<EditingRequestInput, TemplateField>>;
}

/** "No template" in the template picker. */
export const NO_TEMPLATE = 'none';

export const PROJECT_TEMPLATES: ProjectTemplate[] = [
  {
    id: 'social_media',
    name: 'Social media',
    description: 'Short vertical videos for Instagram, TikTok and YouTube Shorts.',
    settings: {
      estimatedComplexity: 'Simple',
      desiredStyle: 'Trendy, fast-paced, eye-catching',
      deliverables: ['Instagram Reel (9:16)', 'TikTok (9:16)', 'YouTube Short (9:16)'],
      musicPreference: 'Trending sounds, upbeat music',
    },
  },
  {
    id: 'corporate',
    name: 'Corporate',
    description: 'Clean, professional videos for presentations and training.',
    settings: {
      estimatedComplexity: 'Moderate',
      desiredStyle: 'Professional, clean, minimalist',
      deliverables: ['Full HD (16:9)', 'Presentation format'],
      musicPreference: 'Corporate background music',
    },
  },
  {
    id: 'creative',
    name: 'Creative',
    description: 'Story-led videos with more effects and a custom soundtrack.',
    settings: {
      estimatedComplexity: 'Complex',
      desiredStyle: 'Creative, artistic, unique',
      deliverables: ['Custom format'],
      musicPreference: 'Custom soundtrack',
    },
  },
];

export const findTemplate = (id?: string | null): ProjectTemplate | undefined =>
  PROJECT_TEMPLATES.find((template) => template.id === id);

const isBlank = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value) && value.length === 0);

const sameValue = (a: unknown, b: unknown): boolean => {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, index) => item === b[index]);
  }
  return a === b;
};

const emptyValue = (field: TemplateField): string | string[] => (field === 'deliverables' ? [] : '');

export interface TemplateApplication {
  request: EditingRequestInput;
  /** Fields the template filled in (or cleared, when switching to no template). */
  changed: TemplateField[];
  /** Fields left alone because the creator had typed their own value. */
  kept: TemplateField[];
}

/**
 * Switch the brief from one template (or none) to another.
 *
 * A field changes only when it is empty or still holds the previous
 * template's value, so a template never overwrites anything the creator
 * typed. Switching to "no template" clears what the previous template filled.
 */
export const applyTemplate = (
  request: EditingRequestInput,
  previousTemplateId: string | null | undefined,
  nextTemplateId: string | null | undefined,
): TemplateApplication => {
  const previous = findTemplate(previousTemplateId);
  const next = findTemplate(nextTemplateId);
  const result: EditingRequestInput = { ...request };
  const changed: TemplateField[] = [];
  const kept: TemplateField[] = [];

  TEMPLATE_FIELDS.forEach((field) => {
    const current = request[field];
    const untouched = isBlank(current) || (previous !== undefined && sameValue(current, previous.settings[field]));
    const target = next ? next.settings[field] : emptyValue(field);
    if (!untouched) {
      if (next) kept.push(field);
      return;
    }
    if (sameValue(current, target) || (isBlank(current) && isBlank(target))) return;
    (result as any)[field] = Array.isArray(target) ? [...target] : target;
    changed.push(field);
  });

  return { request: result, changed, kept };
};

/** "style, music and deliverables". */
export const listFields = (fields: TemplateField[]): string => {
  const words = fields.map((field) => TEMPLATE_FIELD_LABELS[field]);
  if (words.length <= 1) return words.join('');
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
};

/** Trim, drop empties and case-insensitive duplicates, keeping the first spelling. */
export const normalizeDeliverables = (values: Array<string | null | undefined>): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  values.forEach((value) => {
    const text = String(value ?? '').trim();
    const key = text.toLowerCase();
    if (!text || seen.has(key)) return;
    seen.add(key);
    result.push(text);
  });
  return result;
};

// ---- Dates ------------------------------------------------------------------

const pad = (value: number) => String(value).padStart(2, '0');

/** Today's calendar date in the viewer's timezone, as yyyy-mm-dd. */
export const localDateString = (date: Date = new Date()): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** The deadline submit applies when the field is left empty: 7 days from today, in the viewer's timezone. */
export const defaultDeadlineDate = (now: number = Date.now()): string =>
  localDateString(new Date(now + 7 * 24 * 60 * 60 * 1000));

// ---- Asset effects ----------------------------------------------------------

interface EffectLike {
  type?: string | null;
  intensity?: number | null;
  duration?: number | null;
  notes?: string | null;
}

/**
 * Effects as the API's EffectInput takes them: type, intensity and duration
 * only. The form also keeps an id and a note per effect, and sending either
 * made GraphQL refuse the whole project ("Field "id" is not defined by type
 * EffectInput"), so any project with an effect on an image or sound failed.
 */
export const toEffectInputs = (effects?: EffectLike[] | null): Array<{ type: string; intensity: number; duration?: number }> =>
  (effects || [])
    .filter((effect): effect is EffectLike & { type: string } => typeof effect?.type === 'string' && effect.type.trim() !== '')
    .map((effect) => {
      const intensity = Number(effect.intensity);
      const duration = Number(effect.duration);
      return {
        type: effect.type.trim(),
        intensity: Number.isFinite(intensity) ? intensity : 50,
        ...(effect.duration !== null && effect.duration !== undefined && Number.isFinite(duration) ? { duration } : {}),
      };
    });

/** The asset's notes plus each effect's own note ("Fade in: slow at the start"), since EffectInput has no notes. */
export const assetNotesWithEffects = (description?: string | null, effects?: EffectLike[] | null): string =>
  [
    (description || '').trim(),
    ...(effects || [])
      .filter((effect) => effect?.type && effect.notes && effect.notes.trim())
      .map((effect) => `${String(effect.type).trim()}: ${String(effect.notes).trim()}`),
  ]
    .filter(Boolean)
    .join('\n');

// ---- Draft files ------------------------------------------------------------

/**
 * A brief file that isn't in a project yet is deleted 7 days after it
 * uploads. A restored draft drops it a day before that, so it never offers a
 * file that's gone (submitting then failed). The draft's own age doesn't
 * tell: every edit saves it again.
 */
export const DRAFT_FILE_MAX_AGE_MS = 6 * 24 * 60 * 60 * 1000;

/**
 * The files of a restored draft that are still there. A file with no upload
 * time (drafts saved before it was recorded) is kept, as before.
 */
export const keepUnexpiredFiles = <T extends { uploadedAt?: number }>(
  files: unknown,
  now: number = Date.now()
): { kept: T[]; expired: number } => {
  if (!Array.isArray(files)) return { kept: [], expired: 0 };
  const entries = files.filter((file): file is T => Boolean(file) && typeof file === 'object');
  const kept = entries.filter(
    (file) => typeof file.uploadedAt !== 'number' || now - file.uploadedAt <= DRAFT_FILE_MAX_AGE_MS
  );
  return { kept, expired: entries.length - kept.length };
};

/** "2 media files had expired and were removed. Add them again in Media assets." */
export const expiredFilesNotice = (count: number): string =>
  count === 1
    ? `1 media file had expired and was removed. Add it again in ${STEP_LABELS[ASSETS_STEP]}.`
    : `${count} media files had expired and were removed. Add them again in ${STEP_LABELS[ASSETS_STEP]}.`;

// ---- Validation -------------------------------------------------------------

export type FieldKey = 'projectName' | 'video' | 'multicamSet' | 'deadline' | 'editor' | 'description';
export type FieldErrors = Partial<Record<FieldKey, string>>;

/** The order fields appear on screen, so focus goes to the first problem. */
export const FIELD_ORDER: FieldKey[] = ['projectName', 'video', 'multicamSet', 'deadline', 'editor', 'description'];

/** Which step each field lives on. */
export const FIELD_STEP: Record<FieldKey, number> = {
  projectName: 0,
  video: 0,
  multicamSet: 0,
  deadline: 0,
  editor: 0,
  description: 1,
};

export const FIELD_MESSAGES: Record<FieldKey, string> = {
  projectName: 'Enter a project name.',
  video: 'Choose the video you want edited.',
  multicamSet: 'Choose a multicam set.',
  deadline: 'Pick today or a later date.',
  editor: 'Choose who will edit this project.',
  description: 'Describe the edit you want.',
};

export interface BasicsInput {
  projectName?: string;
  sourceType: SourceType;
  videoId?: string;
  multicamSetId?: string;
  editorId?: string;
  deadline?: string;
  /** yyyy-mm-dd in the viewer's timezone. */
  today: string;
}

export const validateBasics = (input: BasicsInput): FieldErrors => {
  const errors: FieldErrors = {};
  if (!input.projectName || input.projectName.trim() === '') errors.projectName = FIELD_MESSAGES.projectName;
  if (input.sourceType === 'SINGLE' && !input.videoId) errors.video = FIELD_MESSAGES.video;
  if (input.sourceType === 'MULTICAM' && !input.multicamSetId) errors.multicamSet = FIELD_MESSAGES.multicamSet;
  // A deadline in the past is never intentional. The native picker's `min`
  // does not stop a typed date, so check it here too.
  if (input.deadline && /^\d{4}-\d{2}-\d{2}$/.test(input.deadline) && input.deadline < input.today) {
    errors.deadline = FIELD_MESSAGES.deadline;
  }
  if (!input.editorId) errors.editor = FIELD_MESSAGES.editor;
  return errors;
};

export const validateBrief = (input: { description?: string }): FieldErrors =>
  !input.description || input.description.trim() === '' ? { description: FIELD_MESSAGES.description } : {};

/** The first field with an error, in on-screen order. */
export const firstInvalidField = (errors: FieldErrors): FieldKey | null =>
  FIELD_ORDER.find((field) => Boolean(errors[field])) ?? null;
